import type { OptOutCell, OptOutColumnKey, OptOutState } from '@ehr/shared';

/**
 * reminders#4 - the Opt-Out Center (Chase's OPT_OUT_CENTER_10-05-V1; Jay 10-08: in the EHR, on
 * core consent). Pure helpers for the page, no DOM.
 */

export interface OptOutRow {
  contactId: string;
  name: string;
  phone: string;
  email: string;
  cells: Record<OptOutColumnKey, OptOutCell>;
}
export interface OptOutList {
  columns: { key: OptOutColumnKey; label: string }[];
  rows: OptOutRow[];
}
export interface ConsentAuditEntry {
  id: string;
  at: string;
  actorId: string | null;
  action: string;
  contactId: string | null;
  contactName: string | null;
  /** ehr#541: every client the line is about (a STOP from a number two clients share names both). */
  contacts?: { id: string; name: string }[];
  /** ehr#541: a STOP / START from a number no client has: its last 4 digits. */
  numberEnding?: string | null;
  detail: Record<string, unknown> | null;
}

/**
 * ehr#541: the Audit log's Client cell: the client(s) to link to their chart, or the words when
 * there is none (a number no client has, or nothing recorded).
 */
export function auditClient(e: ConsentAuditEntry): {
  links: { id: string; name: string }[];
  text: string;
} {
  const links = e.contacts?.length
    ? e.contacts
    : e.contactId && e.contactName
      ? [{ id: e.contactId, name: e.contactName }]
      : [];
  if (links.length) return { links, text: '' };
  if (e.numberEnding) return { links, text: `No client has the number ending ${e.numberEnding}` };
  return { links, text: e.contactName ?? '—' };
}

/** The chart of a client, as a link (the app opens `?contact=` on Contacts with the drawer). */
export const chartHref = (contactId: string) => `/?contact=${encodeURIComponent(contactId)}`;

export const CHIP_TEXT: Record<OptOutState, string> = {
  on: 'On',
  off: 'Off',
  some: 'Some',
  stop: 'STOP',
};

const BOUNCES = new Set(['hard_bounce', 'soft_bounce_limit']);
const SUPPRESSION_TEXT: Record<string, string> = {
  stop: 'texted STOP',
  unsubscribe: 'unsubscribed',
  complaint: 'marked it as spam',
  manual: 'blocked by staff',
  hard_bounce: 'the address bounced',
  soft_bounce_limit: 'the address kept bouncing',
};

const fmtDay = (v: string | Date) =>
  new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

/** What the switch's tooltip says: state, since when, and why. */
export function chipTitle(label: string, cell: OptOutCell): string {
  if (cell.suppression)
    return `${label}: off, ${SUPPRESSION_TEXT[cell.suppression] ?? cell.suppression}`;
  const since = cell.updatedAt ? ` since ${fmtDay(cell.updatedAt)}` : '';
  const why = cell.reason ? ` (${cell.reason})` : '';
  if (cell.state === 'some') return `${label}: on for some messages, off for others${since}`;
  if (!cell.updatedAt) return `${label}: ${cell.state === 'on' ? 'on' : 'off'}, never asked`;
  return `${label}: ${cell.state}${since}${why}`;
}

/**
 * What clicking a switch offers:
 *  - off: turn it off (on, or partly on);
 *  - on: turn it back on (needs the client's documented consent);
 *  - stop: the client texted STOP; back on only with their documented consent;
 *  - bounce: nothing to turn on: the address bounces (fix it in Contact Info).
 */
export type OptOutAction = 'off' | 'on' | 'stop' | 'bounce';
export function actionFor(cell: OptOutCell): OptOutAction {
  if (cell.state === 'stop') return 'stop';
  if (cell.suppression && BOUNCES.has(cell.suppression)) return 'bounce';
  return cell.state === 'off' ? 'on' : 'off';
}

/** Reasons offered when turning a switch off (staff may also type their own). */
export const OFF_REASONS = [
  'Client asked',
  'Wrong number or address',
  'Client no longer in care',
] as const;

/** One audit line, in words. */
export function auditText(e: ConsentAuditEntry, labelOf: (key: string) => string): string {
  const d = e.detail ?? {};
  const str = (k: string) => (typeof d[k] === 'string' ? (d[k] as string) : '');
  switch (e.action) {
    case 'consent.optout.off':
      return `${labelOf(str('column'))} turned off`;
    case 'consent.optout.reenrolled':
      return `${labelOf(str('column'))} turned back on`;
    case 'consent.optout.stop':
      return 'Texted STOP: every text off';
    case 'consent.optout.start':
      return 'Texted START: texts back on';
    case 'consent.unsubscribed':
      return `Unsubscribed from marketing ${str('channel') || 'messages'}`;
    case 'consent.preferences.updated':
      return `Preferences changed (${str('channel') || 'email'})`;
    case 'consent.suppression.added':
      return `Blocked ${str('channel')} (${SUPPRESSION_TEXT[str('reason')] ?? str('reason')})`;
    case 'consent.suppression.removed':
      return 'Block removed';
    default:
      return e.action.startsWith('reminders.optout')
        ? `Reminders: ${e.action.slice('reminders.optout'.length).replace(/^\./, '') || 'opt-out'}`
        : e.action;
  }
}

/** How the client reached us, for the module sources Reminders sends (reminders#4). */
const CLIENT_VIA: Record<string, string> = {
  reminders_sms: 'STOP text',
  reminders_call: 'reminder call',
  reminders_email: 'email link',
  // ehr#541: a STOP / START texted straight to the practice number (core's inbound)
  telnyx_sms: 'text message',
};

/** Who made the change: a staff member, or the client themselves through a module. */
export function auditWho(e: ConsentAuditEntry, userName: (id: string) => string): string {
  if (e.actorId) return userName(e.actorId);
  const src = typeof e.detail?.source === 'string' ? (e.detail.source as string) : '';
  // an opt-out staff had set on Reminders' own roster before this, moved here by Reminders
  if (src === 'reminders_roster') return 'Reminders opt-out roster';
  if (!src) return 'The client';
  return `The client (${CLIENT_VIA[src] ?? src.replace(/_/g, ' ')})`;
}

/** Client search: every word must appear in the name, any order and case. */
export function filterRows(rows: OptOutRow[], text: string): OptOutRow[] {
  const words = text.toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return rows;
  return rows.filter((r) => words.every((w) => r.name.toLowerCase().includes(w)));
}
